import { zodResolver } from '@hookform/resolvers/zod'
import { AcceptInviteInput } from '@numistic/contracts'
import { DataError, useAcceptInvite, useInvite, useSignIn } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Button, Field, Input } from '@numistic/ui'
import { createRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { AuthCard } from '../../components/auth-card.js'
import { useErrorText } from '../../lib/errors.js'
import { rootRoute } from '../root.js'

export const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InvitePage,
})

const FormSchema = AcceptInviteInput.omit({ token: true })

function InvitePage() {
  const { t } = useTranslation()
  const { token } = inviteRoute.useParams()
  const invite = useInvite(token)

  if (invite.isPending) return null

  if (invite.isError || invite.data.status !== 'valid') {
    const status =
      invite.isError || !invite.data
        ? 'invalid'
        : (invite.data.status as 'used' | 'expired' | 'revoked')
    const isNetwork = invite.error instanceof DataError && invite.error.code === 'NETWORK'
    return (
      <AuthCard title={t('invite.title')}>
        <Alert variant="destructive">
          {isNetwork ? t('errors.NETWORK') : t(`invite.status.${status}`)}
        </Alert>
      </AuthCard>
    )
  }

  return <InviteForm token={token} email={invite.data.email} role={invite.data.role} />
}

function InviteForm({ token, email, role }: { token: string; email: string | null; role: string }) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const navigate = useNavigate()
  const accept = useAcceptInvite()
  const signIn = useSignIn()
  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: { name: '', email: email ?? '', password: '' },
  })

  useEffect(() => {
    if (email) form.setValue('email', email)
  }, [email, form])

  const onSubmit = form.handleSubmit(async (values) => {
    await accept.mutateAsync({ token, ...values })
    await signIn.mutateAsync({ email: values.email, password: values.password })
    await navigate({ to: '/' })
  })

  const error = accept.error ?? signIn.error

  return (
    <AuthCard
      title={t('invite.title')}
      description={t('invite.role', { role: t(`roles.${role as 'user'}`) })}
    >
      <form className="grid gap-4" onSubmit={(e) => void onSubmit(e).catch(() => {})} noValidate>
        {error ? <Alert variant="destructive">{errorText.request(error)}</Alert> : null}
        <Field label={t('auth.name')} error={errorText.field(form.formState.errors.name)}>
          <Input autoComplete="name" autoFocus {...form.register('name')} />
        </Field>
        <Field label={t('auth.email')} error={errorText.field(form.formState.errors.email)}>
          <Input
            type="email"
            autoComplete="username"
            readOnly={Boolean(email)}
            {...form.register('email')}
          />
        </Field>
        <Field
          label={t('auth.password')}
          hint={t('auth.passwordHint')}
          error={errorText.field(form.formState.errors.password)}
        >
          <Input type="password" autoComplete="new-password" {...form.register('password')} />
        </Field>
        <Button type="submit" disabled={accept.isPending || signIn.isPending}>
          {t('invite.submit')}
        </Button>
      </form>
    </AuthCard>
  )
}
